import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { GeoBuilder } from './geo.js';
import { routeAt, ROUTE_LEN } from './layout.js';
import { routeY } from './roads.js';
import { makeCanvasTexture } from './textures.js';
import { damp, dampAngle, smoothstep, clamp, wrapAngle, TAU, lerp, chance, rr, rnd } from './util.js';

const rb = (w, h, d, r, seg = 3) => new RoundedBoxGeometry(w, h, d, seg, r);

// ------------------------------------------------------------------ textures
function signTexture() {
  return makeCanvasTexture(1024, 256, (g, w, h) => {
    g.clearRect(0, 0, w, h);
    // pill
    g.fillStyle = '#ff6fa3';
    g.beginPath();
    g.roundRect(6, 10, w - 12, h - 20, 100);
    g.fill();
    g.lineWidth = 12;
    g.strokeStyle = '#fff7e6';
    g.stroke();
    // sprinkles
    const cols = ['#fff7e6', '#ffe14a', '#7fe0c8', '#8ecbff'];
    for (let i = 0; i < 46; i++) {
      g.save();
      g.translate(60 + Math.random() * (w - 120), 30 + Math.random() * (h - 60));
      g.rotate(Math.random() * Math.PI);
      g.fillStyle = cols[i % cols.length];
      g.globalAlpha = 0.9;
      g.beginPath();
      g.roundRect(-9, -3, 18, 6, 3);
      g.fill();
      g.restore();
    }
    g.globalAlpha = 1;
    g.font = '700 150px Fredoka, "Arial Rounded MT Bold", "Trebuchet MS", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    g.lineWidth = 26;
    g.strokeStyle = '#3b2a5c';
    g.strokeText('ICE CREAM', w / 2, h / 2 + 10);
    g.fillStyle = '#fffaf0';
    g.fillText('ICE CREAM', w / 2, h / 2 + 10);
  });
}

function signTexture2() {
  return makeCanvasTexture(384, 288, (g, w, h) => {
    g.fillStyle = '#ff6fa3';
    g.beginPath();
    g.roundRect(6, 6, w - 12, h - 12, 60);
    g.fill();
    g.lineWidth = 12;
    g.strokeStyle = '#fff7e6';
    g.stroke();
    g.font = '700 118px Fredoka, "Arial Rounded MT Bold", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    g.lineWidth = 20;
    g.strokeStyle = '#3b2a5c';
    g.strokeText('ICE', w / 2, 92);
    g.strokeText('CREAM', w / 2, 200);
    g.fillStyle = '#fffaf0';
    g.fillText('ICE', w / 2, 92);
    g.fillText('CREAM', w / 2, 200);
  });
}

function menuTexture() {
  return makeCanvasTexture(256, 320, (g, w, h) => {
    g.fillStyle = '#3b2a5c';
    g.beginPath();
    g.roundRect(0, 0, w, h, 28);
    g.fill();
    g.fillStyle = '#fff7e6';
    g.beginPath();
    g.roundRect(10, 10, w - 20, h - 20, 20);
    g.fill();
    g.fillStyle = '#ff6fa3';
    g.font = '700 38px Fredoka, "Arial Rounded MT Bold", sans-serif';
    g.textAlign = 'center';
    g.fillText('MENU', w / 2, 58);
    const cone = (cx, cy, c1, c2) => {
      g.fillStyle = '#e0a458';
      g.beginPath();
      g.moveTo(cx - 20, cy);
      g.lineTo(cx + 20, cy);
      g.lineTo(cx, cy + 46);
      g.closePath();
      g.fill();
      g.fillStyle = c1;
      g.beginPath();
      g.arc(cx, cy - 4, 24, 0, TAU);
      g.fill();
      if (c2) {
        g.fillStyle = c2;
        g.beginPath();
        g.arc(cx, cy - 26, 19, 0, TAU);
        g.fill();
      }
    };
    cone(60, 128, '#ff8fb8', '#fff0b8');
    cone(128, 128, '#7fe0c8', '#ff8fb8');
    cone(196, 128, '#c9a3ff', null);
    g.fillStyle = '#3b2a5c';
    g.font = '700 30px Fredoka, sans-serif';
    ['★ 1.50', '★ 2.00', '★ 2.50'].forEach((t, i) => g.fillText(t, 60 + i * 68, 228));
    g.font = '700 26px Fredoka, sans-serif';
    g.fillStyle = '#ff6fa3';
    g.fillText('SPRINKLES!', w / 2, 284);
  });
}

// ------------------------------------------------------------------ van
export class Van {
  constructor(scene, stops, notes, puffs) {
    this.scene = scene;
    this.notes = notes;
    this.puffs = puffs;
    this.stops = stops.map((s) => ({ ...s }));
    this.s = 20; // arc length along the route
    this.speed = 0;
    this.cruise = 6.6;
    this.state = 'cruise';
    this.stateT = 0;
    this.stopIdx = -1;
    this.lastStopPassed = -1;
    this.servingLeft = 0;
    this.pitch = 0;
    this.roll = 0;
    this.yaw = 0;
    this.bounce = 0;
    this.bounceV = 0;
    this.wheelSpin = 0;
    this.steer = 0;
    this.prevSpeed = 0;
    this.honkT = 0;
    this.onHonk = null; // callback
    this.onStop = null;
    this.lit = 0; // headlight amount 0..1
    this.hatchOpen = 0;
    this.noteTimer = 0;
    this.smokeTimer = 0;
    this.pos = new THREE.Vector3();
    this._tmp = new THREE.Vector3();
    this._q = new THREE.Quaternion();
    this.beatPrev = 0;

    this.root = new THREE.Group();
    this.root.rotation.order = 'YXZ';
    this.body = new THREE.Group(); // bobs / rolls on the suspension
    this.root.add(this.body);
    this._build();
    scene.add(this.root);
    this._place(0.016, 0);
  }

  // ---------------------------------------------------------------- model
  _build() {
    const b = new GeoBuilder();
    const cream = '#fff0d2', pink = '#ff7fae', pinkLight = '#ffa3c6', mint = '#7fe6cc', dark = '#3a3f55';

    // --- chassis + long pink skirt
    b.add(rb(1.9, 0.3, 4.7, 0.1), 0, 0.62, 0, dark);
    b.add(rb(2.26, 0.66, 4.9, 0.28), 0, 0.93, 0, pink);
    // mint pin-stripe
    b.add(rb(2.285, 0.1, 4.92, 0.05), 0, 1.3, 0, mint);
    // --- cargo box (rear)
    b.add(rb(2.2, 1.28, 2.8, 0.3), 0, 1.98, -1.05, cream);
    // --- hood
    b.add(rb(2.16, 0.5, 1.12, 0.2), 0, 1.15, 1.9, cream);
    b.add(rb(2.2, 0.38, 1.2, 0.16), 0, 1.36, 0.95, cream);
    // --- cab roof + pillars (open windows so the driver is visible)
    b.add(rb(2.2, 0.16, 1.35, 0.08), 0, 2.62, 0.98, cream);
    for (const sx of [-1, 1]) {
      b.add(rb(0.13, 1.1, 0.14, 0.05), sx * 1.03, 2.04, 0.36, cream);
      b.add(rb(0.13, 1.15, 0.14, 0.05), sx * 1.0, 2.04, 1.5, cream, { rx: 0.22 });
    }
    // dashboard + seats (seen through glass)
    b.add(rb(1.9, 0.32, 0.5, 0.08), 0, 1.6, 1.35, '#4a3b6b');
    for (const sx of [-1, 1]) {
      b.add(rb(0.62, 0.74, 0.5, 0.14), sx * 0.5, 1.72, 0.32, '#c94b6e');
      b.add(rb(0.62, 0.16, 0.7, 0.06), sx * 0.5, 1.4, 0.72, '#c94b6e');
    }
    // --- roof frosting + drips
    b.add(rb(2.36, 0.22, 3.0, 0.11), 0, 2.7, -1.05, pinkLight);
    for (let i = 0; i < 12; i++) {
      const z = -2.3 + i * 0.245;
      const len = rr(0.28, 0.6);
      for (const sx of [-1, 1]) {
        b.ico(0.14, 1, sx * 1.15, 2.62 - len / 2, z + rr(-0.03, 0.03), pinkLight, { sy: len / 0.28, sx: 0.85, sz: 0.9 });
      }
    }
    for (let i = 0; i < 7; i++) {
      const x = -0.95 + i * 0.317;
      const len = rr(0.25, 0.5);
      b.ico(0.14, 1, x, 2.62 - len / 2, -2.52, pinkLight, { sy: len / 0.28, sz: 0.85 });
    }
    // sprinkles on the frosting
    const sprk = ['#fff7e6', '#ffe14a', '#7fe6cc', '#8ecbff', '#c9a3ff'];
    for (let i = 0; i < 34; i++) {
      b.box(0.14, 0.05, 0.05, rr(-1.0, 1.0), 2.8, rr(-2.4, 0.3), sprk[i % sprk.length], { ry: rnd() * TAU });
    }
    // --- bumpers, grille, lights
    b.add(rb(2.34, 0.2, 0.34, 0.1), 0, 0.7, 2.5, '#e6ebf3');
    b.add(rb(2.34, 0.2, 0.34, 0.1), 0, 0.7, -2.5, '#e6ebf3');
    b.add(rb(0.95, 0.34, 0.1, 0.05), 0, 0.98, 2.48, '#3a3f55');
    for (let i = 0; i < 3; i++) b.add(rb(0.85, 0.045, 0.06, 0.02), 0, 0.88 + i * 0.1, 2.53, '#e6ebf3');
    b.add(rb(0.5, 0.14, 0.05, 0.03), 0, 0.72, 2.7, '#ffffff');
    // wheel wells (dark discs) + mudguards
    for (const sx of [-1, 1]) {
      for (const z of [1.55, -1.45]) {
        const c = new THREE.CylinderGeometry(0.72, 0.72, 0.12, 20).rotateZ(Math.PI / 2);
        b.add(c, sx * 1.12, 0.58, z, '#20202c');
      }
    }
    // mirrors
    for (const sx of [-1, 1]) {
      b.box(0.05, 0.05, 0.36, sx * 1.2, 1.9, 1.35, '#3a3f55', { rz: sx * 0.4 });
      b.add(rb(0.09, 0.3, 0.22, 0.04), sx * 1.32, 1.98, 1.4, '#e6ebf3');
    }
    // --- serving hatch (-X side): dark opening, counter, striped awning
    b.add(rb(0.1, 0.92, 1.1, 0.03), -1.11, 2.02, -0.7, '#3a2536');
    b.add(rb(0.5, 0.09, 1.3, 0.03), -1.33, 1.52, -0.7, '#fffaf0');
    b.add(rb(0.09, 0.09, 1.3, 0.03), -1.55, 1.52, -0.7, '#ffb3cf');
    // little tubs of ice cream on the counter
    const tub = ['#ff8fb8', '#7fe6cc', '#fff0b8', '#c9a3ff'];
    for (let i = 0; i < 3; i++) {
      b.cyl(0.13, 0.11, 0.14, 8, -1.34, 1.57, -1.05 + i * 0.36, '#ffffff');
      b.sphere(0.13, -1.34, 1.74, -1.05 + i * 0.36, tub[i], { sy: 0.8 });
    }
    // awning (striped, sloping outward)
    const nStripes = 7;
    for (let i = 0; i < nStripes; i++) {
      const z = -0.7 - 0.7 + (i + 0.5) * (1.4 / nStripes);
      const col = i % 2 ? '#fff7e6' : '#ff6fa3';
      b.box(0.9, 0.06, 1.4 / nStripes, -1.5, 2.44, z, col, { rz: 0.32 });
      b.box(0.06, 0.24, 1.4 / nStripes, -1.93, 2.2, z, col);
    }
    for (const z of [-1.38, -0.02]) b.box(0.05, 0.8, 0.05, -1.86, 1.55, z, '#e6ebf3');

    this.bodyGeo = b.build();
    this.bodyMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.42, metalness: 0.02 });
    const bodyMesh = new THREE.Mesh(this.bodyGeo, this.bodyMat);
    bodyMesh.castShadow = true;
    bodyMesh.receiveShadow = true;
    this.body.add(bodyMesh);

    // --- glass
    const glassMat = new THREE.MeshStandardMaterial({ color: '#aee0ff', roughness: 0.08, metalness: 0.0, transparent: true, opacity: 0.32, depthWrite: false, emissive: '#5aa0e0', emissiveIntensity: 0.12 });
    const gb = new GeoBuilder();
    const wsGeo = new THREE.BoxGeometry(1.85, 0.98, 0.04);
    const glass = new THREE.Mesh(wsGeo, glassMat);
    glass.position.set(0, 2.05, 1.6);
    glass.rotation.x = -0.24;
    this.body.add(glass);
    for (const sx of [-1, 1]) {
      const g = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.98, 1.1), glassMat);
      g.position.set(sx * 1.03, 2.05, 0.95);
      this.body.add(g);
    }
    // (rear window of cab is the cargo box front)

    // --- decals
    const signMat = new THREE.MeshBasicMaterial({ map: signTexture(), transparent: true, toneMapped: true, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    signMat.color.setScalar(0.92);
    {
      // long sign on the +X side
      const p = new THREE.Mesh(new THREE.PlaneGeometry(2.3, 0.575), signMat);
      p.position.set(1.115, 2.0, -1.05);
      p.rotation.y = Math.PI / 2;
      this.body.add(p);
      // stacked sign on the hatch side (rear of the window)
      const sm2 = new THREE.MeshBasicMaterial({ map: signTexture2(), transparent: true, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
      sm2.color.setScalar(0.92);
      const q = new THREE.Mesh(new THREE.PlaneGeometry(0.95, 0.72), sm2);
      q.position.set(-1.115, 2.05, -1.9);
      q.rotation.y = -Math.PI / 2;
      this.body.add(q);
      const r = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 0.42), signMat);
      r.position.set(0, 2.02, -2.462);
      r.rotation.y = Math.PI;
      this.body.add(r);
      const menuMat = new THREE.MeshBasicMaterial({ map: menuTexture(), polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
      menuMat.color.setScalar(0.92);
      const m = new THREE.Mesh(new THREE.PlaneGeometry(0.62, 0.68), menuMat);
      m.position.set(-1.145, 0.96, -0.7);
      m.rotation.y = -Math.PI / 2;
      this.body.add(m);
    }

    // --- glowing lights (headlights + taillights)
    this.glowMat = new THREE.MeshBasicMaterial({ color: new THREE.Color('#fff4c8').multiplyScalar(0.95) });
    this.tailMat = new THREE.MeshBasicMaterial({ color: new THREE.Color('#ff3b4e') });
    const hl = new THREE.SphereGeometry(0.24, 12, 10);
    for (const sx of [-1, 1]) {
      const h = new THREE.Mesh(hl, this.glowMat);
      h.scale.set(1, 1, 0.55);
      h.position.set(sx * 0.82, 1.0, 2.45);
      this.body.add(h);
      const rimGeo = new THREE.TorusGeometry(0.25, 0.04, 6, 16);
      const rim = new THREE.Mesh(rimGeo, new THREE.MeshStandardMaterial({ color: '#e6ebf3', roughness: 0.3 }));
      rim.position.set(sx * 0.82, 1.0, 2.5);
      this.body.add(rim);
      const t = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.16, 0.06), this.tailMat);
      t.position.set(sx * 0.88, 1.1, -2.47);
      this.body.add(t);
    }

    // --- roof topper: giant ice cream cone
    this.topper = new THREE.Group();
    this.topper.position.set(0, 2.8, -1.05);
    const tb = new GeoBuilder();
    tb.cone(0.62, 1.5, 14, 0, 1.5, 0, '#e2a45e', { rx: Math.PI, jitter: 0.16 });
    for (let i = 0; i < 6; i++) {
      const y = 0.3 + i * 0.2;
      tb.cyl(0.62 * (y / 1.5) + 0.01, 0.62 * (y / 1.5) + 0.01, 0.03, 14, 0, y, 0, '#b97a38');
    }
    tb.sphere(0.72, 0, 1.75, 0, '#ff8fb8', { sy: 0.9 });
    tb.sphere(0.64, 0, 2.5, 0, '#7fe6cc', { sy: 0.9 });
    tb.sphere(0.5, 0, 3.15, 0, '#fff0b8', { sy: 0.9 });
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU + 0.2;
      const len = rr(0.2, 0.45);
      tb.ico(0.13, 1, Math.cos(a) * 0.68, 1.62 - len / 2, Math.sin(a) * 0.68, '#ff8fb8', { sy: len / 0.26 });
    }
    tb.sphere(0.18, 0.05, 3.65, 0, '#e63946');
    tb.cyl(0.02, 0.02, 0.28, 4, 0.05, 3.7, 0, '#4f8f3a', { rz: -0.4 });
    for (let i = 0; i < 26; i++) {
      const a = rnd() * TAU, e = rr(0.0, 1.2), sc = pick3();
      const R = [0.72, 0.64, 0.5][sc], cy = [1.75, 2.5, 3.15][sc];
      tb.box(0.13, 0.045, 0.045, Math.cos(a) * Math.cos(e) * R, cy + Math.sin(e) * R * 0.9, Math.sin(a) * Math.cos(e) * R, ['#fff7e6', '#ffe14a', '#8ecbff', '#c9a3ff', '#ff5d8f'][i % 5], { ry: -a, rz: e });
    }
    function pick3() { return Math.floor(rnd() * 3); }
    const topMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.4, metalness: 0 });
    const topMesh = new THREE.Mesh(tb.build(), topMat);
    topMesh.castShadow = true;
    this.topper.add(topMesh);
    this.topper.scale.setScalar(0.8);
    this.body.add(this.topper);

    // --- speakers on the roof
    const spMat = new THREE.MeshStandardMaterial({ color: '#dfe5ee', roughness: 0.35 });
    this.speakers = [];
    for (const sx of [-1, 1]) {
      const sp = new THREE.Group();
      const horn = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.09, 0.42, 12, 1, true), new THREE.MeshStandardMaterial({ color: '#dfe5ee', roughness: 0.35, side: THREE.DoubleSide }));
      horn.rotation.x = Math.PI / 2 - 0.15;
      horn.position.z = 0.16;
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.14, 0.2, 8), spMat);
      base.position.y = -0.1;
      const inner = new THREE.Mesh(new THREE.CircleGeometry(0.26, 12), new THREE.MeshBasicMaterial({ color: '#6a5f92' }));
      inner.position.set(0, 0.04, 0.37);
      inner.rotation.x = -0.15;
      sp.add(horn, base, inner);
      sp.position.set(sx * 0.7, 2.96, 0.35);
      this.body.add(sp);
      this.speakers.push(sp);
    }

    // --- driver
    this.driver = new THREE.Group();
    this.driver.position.set(0.5, 1.5, 0.55);
    const skin = new THREE.MeshStandardMaterial({ color: '#ffd3ad', roughness: 0.7 });
    const white = new THREE.MeshStandardMaterial({ color: '#fffaf0', roughness: 0.6 });
    const pinkM = new THREE.MeshStandardMaterial({ color: '#ff6fa3', roughness: 0.6 });
    const brown = new THREE.MeshStandardMaterial({ color: '#5a3a24', roughness: 0.8 });
    const black = new THREE.MeshBasicMaterial({ color: '#241a2c' });
    const torso = new THREE.Mesh(rb(0.6, 0.7, 0.42, 0.16), white);
    torso.position.set(0, 0.25, 0);
    const apron = new THREE.Mesh(rb(0.36, 0.5, 0.05, 0.02), pinkM);
    apron.position.set(0, 0.22, 0.24);
    this.head = new THREE.Group();
    this.head.position.set(0, 0.86, 0.02);
    const skull = new THREE.Mesh(new THREE.SphereGeometry(0.3, 16, 12), skin);
    const nose = new THREE.Mesh(new THREE.SphereGeometry(0.075, 8, 6), new THREE.MeshStandardMaterial({ color: '#ffb59a', roughness: 0.6 }));
    nose.position.set(0, -0.02, 0.3);
    const stache = new THREE.Group();
    for (const sx of [-1, 1]) {
      const m = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6), brown);
      m.scale.set(1.4, 0.6, 0.8);
      m.position.set(sx * 0.11, -0.1, 0.27);
      m.rotation.z = sx * -0.35;
      stache.add(m);
    }
    const eyes = new THREE.Group();
    for (const sx of [-1, 1]) {
      const e = new THREE.Mesh(new THREE.SphereGeometry(0.055, 8, 6), black);
      e.position.set(sx * 0.12, 0.08, 0.27);
      eyes.add(e);
      const ch = new THREE.Mesh(new THREE.CircleGeometry(0.06, 8), new THREE.MeshBasicMaterial({ color: '#ff9ab0', transparent: true, opacity: 0.7 }));
      ch.position.set(sx * 0.2, -0.05, 0.25);
      ch.rotation.y = sx * 0.6;
      eyes.add(ch);
    }
    const hat = new THREE.Group();
    const hatTop = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.26, 0.2, 12), white);
    hatTop.position.y = 0.32;
    const hatBand = new THREE.Mesh(new THREE.CylinderGeometry(0.265, 0.265, 0.07, 12), pinkM);
    hatBand.position.y = 0.24;
    const pom = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 6), pinkM);
    pom.position.y = 0.46;
    hat.add(hatTop, hatBand, pom);
    this.head.add(skull, nose, stache, eyes, hat);
    const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.03, 6, 14), new THREE.MeshStandardMaterial({ color: '#2a2a38' }));
    wheel.position.set(0, 0.0, 0.95 - 0.55);
    wheel.rotation.x = -0.9;
    this.driver.add(torso, apron, this.head);
    this.body.add(this.driver);
    this.steerWheel = wheel;
    wheel.position.set(0.5, 1.72, 1.02);
    this.body.add(wheel);
    for (const sx of [-1, 1]) {
      const hand = new THREE.Mesh(new THREE.SphereGeometry(0.075, 8, 6), skin);
      hand.position.set(0.5 + sx * 0.2, 1.72, 1.02);
      this.body.add(hand);
    }

    // --- wheels (children of root: they do not follow the body's bounce)
    this.wheels = [];
    const tyreGeo = new THREE.CylinderGeometry(0.56, 0.56, 0.4, 22).rotateZ(Math.PI / 2);
    const tyreMat = new THREE.MeshStandardMaterial({ color: '#2b2b38', roughness: 1 });
    const hubGeo = new THREE.CylinderGeometry(0.34, 0.34, 0.42, 16).rotateZ(Math.PI / 2);
    const hubMat = new THREE.MeshStandardMaterial({ color: '#f2f4f8', roughness: 0.4 });
    const capGeo = new THREE.CylinderGeometry(0.13, 0.13, 0.46, 10).rotateZ(Math.PI / 2);
    const capMat = new THREE.MeshStandardMaterial({ color: '#ff6fa3', roughness: 0.4 });
    const barGeo = new THREE.BoxGeometry(0.44, 0.08, 0.5);
    const barMat = new THREE.MeshStandardMaterial({ color: '#8f95a8', roughness: 0.5 });
    for (const [wx, wz, front] of [[-1.03, 1.55, true], [1.03, 1.55, true], [-1.03, -1.45, false], [1.03, -1.45, false]]) {
      const pivot = new THREE.Group();
      pivot.position.set(wx, 0.56, wz);
      const spin = new THREE.Group();
      const tyre = new THREE.Mesh(tyreGeo, tyreMat);
      const hub = new THREE.Mesh(hubGeo, hubMat);
      const cap = new THREE.Mesh(capGeo, capMat);
      spin.add(tyre, hub, cap);
      for (let k = 0; k < 3; k++) {
        const bar = new THREE.Mesh(barGeo, barMat);
        bar.rotation.x = (k * Math.PI) / 3;
        bar.scale.set(1.02, 1, 1);
        bar.position.x = wx < 0 ? -0.0 : 0.0;
        spin.add(bar);
      }
      tyre.castShadow = true;
      pivot.add(spin);
      this.root.add(pivot);
      this.wheels.push({ pivot, spin, front });
    }

    // --- headlight spot (night)
    this.spot = new THREE.SpotLight('#fff0c0', 0, 46, 0.62, 0.65, 1.3);
    this.spot.position.set(0, 1.1, 2.3);
    this.spot.target.position.set(0, -0.5, 14);
    this.root.add(this.spot, this.spot.target);
    this.glowMats = [this.glowMat, this.tailMat];
  }

  // ---------------------------------------------------------------- placement helpers
  /** world position of a van-local point (uses root's current matrix, body offsets ignored). */
  local(x, y, z, out = new THREE.Vector3()) {
    return out.set(x, y, z).applyMatrix4(this.root.matrixWorld);
  }

  /** the queue spot beside the serving hatch (on the -X side), on the ground */
  hatchSpot(k = 0, out = new THREE.Vector3()) {
    const sy = Math.sin(this.yaw), cy = Math.cos(this.yaw);
    // local (-2.7 - k*0.0, 0, -0.95 + offset) -> world
    const lx = -2.8, lz = -0.95 + (k - 1.5) * 0.85;
    out.set(this.pos.x + lx * cy + lz * sy, 0, this.pos.z - lx * sy + lz * cy);
    return out;
  }

  honk() {
    this.honkT = 0.5;
    this.bounceV += 1.6;
    if (this.onHonk) this.onHonk();
    for (let i = 0; i < 5; i++) {
      const p = this.local((i % 2 ? 1 : -1) * 0.7, 3.2, 0.4, this._tmp);
      this.notes.emit(p.x, p.y, p.z, rr(-1, 1), rr(-1, 1));
    }
  }

  // ---------------------------------------------------------------- simulation
  _place(dt, beat) {
    const r = routeAt(this.s);
    const y0 = routeY(this.s);
    this.pos.set(r.x, y0, r.z);
    this.yaw = r.yaw;
    this.curv = r.curv;
    const wb = 1.6;
    const yF = routeY(this.s + wb), yB = routeY(this.s - wb);
    this.roadPitch = Math.atan2(yF - yB, wb * 2);
  }

  update(dt, time, beat, night) {
    // ---- state machine: cruise / brake into stop / serve / go
    const accel = 2.6, decel = 3.2;
    let target = this.cruise;
    // slow gently for bends
    const curv = Math.abs(this.curv || 0);
    target = Math.min(target, Math.sqrt(4.5 / Math.max(curv, 0.0001)));

    if (this.state === 'serving') {
      target = 0;
      this.servingLeft -= dt;
      this.hatchOpen = damp(this.hatchOpen, 1, 6, dt);
      if (this.servingLeft <= 0) { this.state = 'cruise'; this.hatchOpen = 0; }
    } else {
      this.hatchOpen = damp(this.hatchOpen, 0, 6, dt);
      // look for the next stop
      let best = null, bestD = 1e9;
      for (let i = 0; i < this.stops.length; i++) {
        const st = this.stops[i];
        let d = st.s - this.s;
        d = ((d % ROUTE_LEN) + ROUTE_LEN) % ROUTE_LEN;
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best !== null && this.stopIdx !== best && bestD < 60) {
        // decide once per approach whether to stop here
        this.stopIdx = best;
        this.stopThis = chance(0.5);
      }
      if (this.stopThis && best === this.stopIdx) {
        const brakeD = (this.speed * this.speed) / (2 * decel) + 1.0;
        if (bestD < brakeD + 8) {
          // ease into the stop: speed proportional to remaining distance
          target = Math.min(target, Math.max(0, Math.sqrt(2 * decel * Math.max(bestD - 0.4, 0))));
        }
        if (bestD < 0.6 && this.speed < 0.6) {
          this.state = 'serving';
          this.servingLeft = rr(5, 7.5);
          this.stopThis = false;
          this.honk();
          if (this.onStop) this.onStop(this.stops[best]);
          // do not re-trigger the same stop immediately
          this.lastStopPassed = best;
        }
      }
    }
    // speed integration
    const dv = target - this.speed;
    const a = dv > 0 ? accel : decel;
    this.speed += clamp(dv, -a * dt, a * dt);
    if (this.speed < 0.02 && target === 0) this.speed = 0;
    const acc = (this.speed - this.prevSpeed) / Math.max(dt, 1e-4);
    this.prevSpeed = this.speed;
    this.s = (this.s + this.speed * dt) % ROUTE_LEN;
    this._place(dt, beat);

    // ---- root transform
    this.root.position.copy(this.pos);
    this.root.rotation.y = this.yaw;

    // suspension: pitch from road slope + brake dive, roll from lateral accel
    const latAcc = this.speed * this.speed * (this.curv || 0);
    const pitchT = this.roadPitch - clamp(acc * 0.012, -0.05, 0.05) * 1.0;
    const rollT = clamp(latAcc * 0.012, -0.07, 0.07);
    this.pitch = damp(this.pitch, pitchT, 7, dt);
    this.roll = damp(this.roll, rollT, 6, dt);
    this.root.rotation.x = -this.pitch;
    this.root.rotation.z = this.roll;

    // beat bounce: a spring driven by the music
    const bf = beat % 1;
    if (bf < this.beatPrev) this.bounceV += 1.4 + (this.state === 'serving' ? 0.8 : 0);
    this.beatPrev = bf;
    const k = 180, c = 11;
    this.bounceV += (-k * this.bounce - c * this.bounceV) * dt;
    this.bounce += this.bounceV * dt;
    const bob = clamp(this.bounce, -0.12, 0.2);
    // road rumble
    const rumble = Math.sin(time * 27) * 0.006 * Math.min(1, this.speed / 5);
    this.body.position.y = bob * 0.12 + rumble;
    this.body.scale.set(1 - bob * 0.05, 1 + bob * 0.09, 1 - bob * 0.04);
    this.body.rotation.z = Math.sin(time * 2.1) * 0.006 + bob * 0.0;

    // wheels
    this.wheelSpin += (this.speed * dt) / 0.56;
    const steerT = clamp((this.curv || 0) * 3.0, -0.55, 0.55);
    this.steer = damp(this.steer, steerT, 8, dt);
    for (const w of this.wheels) {
      w.spin.rotation.x = this.wheelSpin;
      if (w.front) w.pivot.rotation.y = this.steer;
    }
    this.steerWheel.rotation.z = -this.steer * 3.2;

    // topper: slow spin + beat wobble
    this.topper.rotation.y += dt * (0.9 + this.hatchOpen * 2.0);
    this.topper.rotation.z = Math.sin(time * 2.4) * 0.05 + this.bounce * 0.35;
    this.topper.rotation.x = Math.cos(time * 1.9) * 0.04;
    this.topper.position.y = 2.8 + Math.max(0, bob) * 0.6;

    // driver bops
    this.head.rotation.z = Math.sin(time * 4.0) * 0.12 + bob * 0.6;
    this.head.rotation.y = Math.sin(time * 1.3) * 0.25 + this.steer * 0.8;
    this.head.position.y = 0.86 + Math.max(0, bob) * 0.5;

    // lights
    this.lit = damp(this.lit, smoothstep(0.25, 0.6, night), 3, dt);
    this.spot.intensity = this.lit * 260;
    this.glowMat.color.setRGB(1, 0.96, 0.78).multiplyScalar(0.9 + this.lit * 3.2);
    this.tailMat.color.setRGB(1, 0.2, 0.28).multiplyScalar(0.7 + this.lit * 1.8 + (this.speed < this.prevSpeed - 0.001 ? 1.3 : 0));

    // honk squash timer
    if (this.honkT > 0) this.honkT -= dt;

    // ---- particles: notes from the speakers + exhaust puffs
    this.root.updateMatrixWorld(true);
    this.noteTimer -= dt;
    if (this.noteTimer <= 0) {
      this.noteTimer = 0.42 + rnd() * 0.25;
      const sp = this.speakers[Math.floor(rnd() * 2)];
      const p = sp.getWorldPosition(this._tmp);
      this.notes.emit(p.x, p.y + 0.3, p.z, 0, 0);
    }
    this.smokeTimer -= dt;
    if (this.smokeTimer <= 0 && this.speed > 0.4) {
      this.smokeTimer = 0.13;
      const p = this.local(0.75, 0.55, -2.7, this._tmp);
      const back = -this.speed * 0.25;
      this.puffs.emit(p.x, p.y, p.z, Math.sin(this.yaw) * back + rr(-0.2, 0.2), rr(0.35, 0.8), Math.cos(this.yaw) * back + rr(-0.2, 0.2), rr(0.7, 1.1), 0.06, rr(0.16, 0.26));
    }
  }
}
